/**
 * HAR.com native filters the AI Mode filters panel has no control of its own for.
 *
 * `SearchListingsOperationModel.convertNativeToFilters` detaches every native
 * query-string key it understands and stashes whatever is left in
 * `filters.extra`. That bag is persisted server-side behind an opaque
 * `base_filters_id` (`search_listings_operation_model.py`) and stays applied to
 * every follow-up search, so a search can be scoped by criteria that never
 * appear anywhere in the UI — the pill row only counts them
 * ("+ N HAR.com filters"). This module turns that bag into readable rows so
 * they can be read and dropped individually.
 *
 * Display names are not here: `HarExtraFilters` reads them from HAR.com's own
 * search form via `useListingFilterLabels`, falling back to `humanizeKey`.
 */

/** Native keys that ride along in the query string without narrowing the search
 *  — paging, sorting and search-form/view plumbing. Listing them as filters
 *  would invite dropping them, which changes nothing about the results. */
const IGNORED_KEYS: readonly string[] = [
  "view",
  "showform",
  "showmap",
  "page",
  "start",
  "max",
  "limit",
  "sort",
];

export interface IHarExtraFilterEntry {
  /** The native query-string key, e.g. `dom_max`. */
  key: string;
  /** The raw value, kept so unchecking a row can be undone. */
  value: string;
}

/** `listed_today` -> "Listed Today". The last resort for a native key HAR.com's
 *  search form has no label for, so an unknown filter still reads as words. */
export const humanizeKey = (key: string): string =>
  key
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * The HAR.com passthrough filters worth showing.
 *
 * Empty values are dropped: `convertNativeToFilters` hands the leftover native
 * object over as-is, blank query-string keys included (`ptids: ""`), and those
 * filter nothing.
 */
export const getHarExtraFilterEntries = (
  extra?: Record<string, string>,
): IHarExtraFilterEntry[] => {
  if (!extra) return [];

  return Object.entries(extra)
    .filter(([key, value]) => {
      if (IGNORED_KEYS.includes(key)) return false;
      return value != null && String(value).trim() !== "";
    })
    .map(([key, value]) => ({ key, value: String(value) }));
};

/**
 * Passthrough params the UI reasons about by name, rather than merely listing.
 *
 * The bag itself stays open — `convertNativeToFilters` builds it by subtraction,
 * so it holds keys no type can enumerate — and naming one here does NOT model
 * it: the value is still replayed verbatim server-side and still shows in the
 * rows above. The list exists so a rule that depends on one of these keys can be
 * written as a compile-checked reference (`extra.<key>` in FilterPills'
 * `hideWhen`) instead of a bare string that silently rots on a typo.
 *
 * Since CB-1509 the drive-time family that seeded it (CB-1765) is modeled —
 * `SearchListingsToolInput` declares the four `drivetime_*` fields, so
 * `convertNativeToFilters` detaches them, the UI converters promote the copies
 * older artifacts keep in `extra` (promoteLegacyDriveTime), and FilterPills
 * reads them by name. The list is empty until the next passthrough key needs a
 * rule.
 */
export const KNOWN_EXTRA_FILTERS = [] as const;

export type KnownExtraFilter = (typeof KNOWN_EXTRA_FILTERS)[number];
